/**
 * Tenant API client — Section 2 Cycle 1 (Company Details modal).
 *
 * Two thin wrappers over apiFetch matching:
 *   GET   /api/tenant/company-details
 *   PATCH /api/tenant/company-details
 *
 * Types mirror apps/api/src/services/tenant/tenantService.ts — keep them in
 * sync when the API payload changes.
 */

import { apiFetch } from './api';

export interface CompanyDetails {
  /** DH-COMPDOC-ADDR-001 — composed single-line address, recomposed server-side
   *  from the four parts below. Still read by contracts / PDF / compliance. */
  registeredAddress: string | null;
  registeredAddressLine1: string | null;
  registeredAddressLine2: string | null;
  registeredAddressCity: string | null;
  registeredAddressPostcode: string | null;
  /** 2026-08-17 — how to reach the company. Captured once here; the board
   *  rating plate and anything else needing a contact line reads these
   *  rather than keeping its own copy. */
  companyTelephone: string | null;
  companyEmail: string | null;
  companyNumber: string | null;
  vatNumber: string | null;
  icoRegistrationNumber: string | null;
  eoriNumber: string | null;
  employersLiabilityInsuranceNumber: string | null;
  /** DH-INS-001 — EL policy expiry (ISO yyyy-mm-dd). Drives the
   *  dashboard Business Insurance card status. */
  employersLiabilityInsuranceExpiry: string | null;
  /** DH-INS-002 — insurer name + cover/indemnity limit (free text,
   *  e.g. "NIG" / "£10,000,000"). AI-read from the filed certificate. */
  employersLiabilityInsuranceInsurer: string | null;
  employersLiabilityInsuranceCover: string | null;
  publicLiabilityInsuranceNumber: string | null;
  /** DH-INS-001 — PL policy expiry (ISO yyyy-mm-dd). */
  publicLiabilityInsuranceExpiry: string | null;
  /** DH-INS-002 — PL insurer + cover. */
  publicLiabilityInsuranceInsurer: string | null;
  publicLiabilityInsuranceCover: string | null;
  /** DH-COMPDOC-INS-001 — Products Liability (defective products). Fed by a
   *  standalone products policy or a collective policy's Products cover. */
  productsLiabilityInsuranceNumber: string | null;
  productsLiabilityInsuranceExpiry: string | null;
  productsLiabilityInsuranceInsurer: string | null;
  productsLiabilityInsuranceCover: string | null;
  fleetInsuranceNumber: string | null;
  /** DH-INS-001 — company-level fleet policy expiry. Distinct from
   *  per-vehicle insuranceExpiry (Fleet Vehicle Insurance card). */
  fleetInsuranceExpiry: string | null;
  /** DH-INS-002 — fleet policy insurer + cover. */
  fleetInsuranceInsurer: string | null;
  fleetInsuranceCover: string | null;
  oLicenceNumber: string | null;
  logoUrl: string | null;
  /**
   * The same logo at an address anybody can fetch — the server derives it
   * from the stored data URL, which a mail client would strip. Read-only:
   * it is not stored and a PATCH ignores it.
   */
  logoPublicUrl?: string | null;
  /** DH-LIVE-011 — pension auto-enrolment staging date (ISO YYYY-MM-DD). */
  pensionsStagingDate: string | null;
}

export type CompanyDetailsInput = Partial<CompanyDetails>;

/**
 * The company directory waiting list.
 *
 * The directory is not built. `registeredAt` null means this tenant has not
 * asked to be on the list; a date means they have, and the card says so rather
 * than offering the same button twice.
 */
export interface DirectoryInterest {
  registeredAt: string | null;
}

export async function fetchDirectoryInterest(
  signal?: AbortSignal,
): Promise<DirectoryInterest> {
  return apiFetch<DirectoryInterest>('/api/tenant/directory-interest', {
    method: 'GET',
    signal,
  });
}

export async function registerDirectoryInterest(): Promise<DirectoryInterest> {
  return apiFetch<DirectoryInterest>('/api/tenant/directory-interest', {
    method: 'POST',
  });
}

/** What a company is asked to agree to before it can use the platform. */
export interface AgreementToSign {
  /** True for a director of a company that has not agreed to the terms as published. */
  required: boolean;
  /** The last day the director can carry on without signing; null when nothing is required. */
  signBy: string | null;
  /** Days left before signBy runs out: 0 is the last day. */
  daysLeft: number | null;
  /** True once signBy has passed: no carrying on until they sign. */
  mustSignNow: boolean;
  companyName: string;
  termsUrl: string;
  termsVersion: string;
  privacyUrl: string;
  privacyVersion: string;
  marketingWording: string;
  /** False when JeanieIQ has published no terms: nobody is held to nothing. */
  ready: boolean;
}

export async function fetchAgreementToSign(signal?: AbortSignal): Promise<AgreementToSign> {
  return apiFetch<AgreementToSign>('/api/tenant/agreement', { method: 'GET', signal });
}

/** A director agrees for the company. The tick is its own answer. */
export async function signAgreement(marketingConsent: boolean): Promise<{ agreedAt: string }> {
  return apiFetch<{ agreedAt: string }>('/api/tenant/agreement', {
    method: 'POST',
    body: { marketingConsent },
  });
}
export async function fetchCompanyDetails(signal?: AbortSignal): Promise<CompanyDetails> {
  return apiFetch<CompanyDetails>('/api/tenant/company-details', {
    method: 'GET',
    signal,
  });
}

export async function updateCompanyDetails(
  input: CompanyDetailsInput,
): Promise<CompanyDetails> {
  return apiFetch<CompanyDetails>('/api/tenant/company-details', {
    method: 'PATCH',
    body: input,
  });
}

// DH-023 / DH-025 — minimal current-tenant profile.
export interface TenantProfile {
  id: string;
  name: string;
  slug: string;
  /**
   * 2026-10-01 — whether this tenant requires an approver to be matched to an
   * employee record before they can approve leave. Default false.
   *
   * The self-approval refusal recognises only matched accounts, so unmatched
   * it fails open. This says whether that is refused or merely flagged.
   */
  requireEmployeeMatchToApprove: boolean;
  /** Over a statutory leave limit: warn and let the booker confirm, or refuse. */
  leaveLimitMode: LeaveLimitMode;
  sector: string;
  /**
   * The sector as a person reads it - "Switchboard Manufacturing", not
   * `switchboard_manufacturing`. Resolved server-side from the
   * `country_sectors` taxonomy, so it is the real label and not a
   * title-cased key: `fleet_transport` reads "Fleet / Transport".
   */
  sectorLabel: string;
  countryCode: string;
  /**
   * Sidebar labelKeys this tenant should NOT see. Empty array means
   * the sidebar shows every section (default). Sidebar.tsx filters
   * NAV_GROUPS against this at render time.
   */
  hiddenSections: string[];
  /**
   * Multi-site flag (2026-05-08). When true, site-aware UIs reveal per-
   * site tabs, allocations, and tablet-to-site mapping. When false
   * (default), the platform behaves as it always has — single-site.
   */
  isMultiSite: boolean;
  /**
   * Stage 1.6e (2026-05-12) — biometric authentication master toggle.
   * Default false; director enables after DPIA. When false, the My
   * Profile Biometric tab renders a notice instead of the enrol form,
   * and the kiosk hides the biometric buttons.
   */
  biometricEnabled: boolean;
  /**
   * 2026-05-13 — Rota → Shift Management tab visibility. Default true.
   * Per-tenant override (currently flipped manually by JeanieIQ staff —
   * future JeanieIQ Admin Page). When false, RotaPage omits the
   * Shift Management tab button.
   */
  shiftManagementEnabled: boolean;
  /**
   * "OVERRIDE ALL SHIFT patterns on" (2026-08-30) - the PLATFORM switch, echoed
   * onto this profile so the sidebar and RotaPage read one object. Not a tenant
   * setting: no tenant-side screen can change it. When true, the
   * shiftManagementEnabled gate above and the pharmacy sector rule are both
   * bypassed for VISIBILITY only.
   */
  overrideAllShiftPatterns: boolean;
  /** This company is the Operator (Platform Admin > Settings). Shows the operator-only Sales tabs. */
  isOperator: boolean;
  /**
   * 2026-05-13 — Finance group add-on modules. Each gates a sidebar
   * section. All default false (paid add-ons). Sidebar hides the nav
   * entry when the flag is false; routes still exist but are unreachable
   * without the flag.
   */
  payrollEnabled: boolean;
  subContractEnabled: boolean;
  cashflowEnabled: boolean;
  /** Finance -> Tronc paid add-on. Gates the sidebar entry. */
  troncEnabled: boolean;
  /**
   * Clinical modules switched on for this tenant (2026-10-01, CS-02 HM-01):
   * CLINICAL_MODULES keys a JeanieIQ platform admin enabled after the CSO
   * signed the module into the safety case. The API refuses the rest regardless;
   * this hides their tiles. Optional for cached payloads (= none on).
   */
  clinicalModules?: string[];
  auditPackEnabled: boolean;
  /** JN-DIR-SUBS-001 — subscription metadata for the /admin/subscription
   *  page. Optional on the type because older callers may not refetch
   *  after the payload was extended; new code can safely use ?? '—'. */
  subscriptionTier?: string;
  subscriptionStatus?: string;
  trialEndsAt?: string | null;
  agentsEnabled?: boolean;
  /**
   * JN-AI-FLAG-001 (2026-05-28) — Plus-tier paid add-on. When false,
   * the AI document-filler buttons (RA generator + document-tagger
   * upload flows) render greyed-out with "Upgrade to Plus" tooltip
   * and the backend endpoints return 403 AI_ASSISTANCE_DISABLED.
   * Ask Jeanie chat is NOT gated by this flag.
   */
  aiAssistanceEnabled?: boolean;
  /**
   * 2026-06-12 (Shane) — per-section enabled tiers from the Platform
   * Admin Sidebar-sections modal. Key = section catalog key, value =
   * enabled tiers. A key ABSENT from the map = the default, Base only.
   * Optional on the type for older cached payloads; consumers go
   * through useSectionTiers which treats undefined as the default.
   */
  sectionTiers?: Record<string, string[]>;
  /** Sections this tenant HAS. Empty = not set up; the sidebar falls back. */
  selectedSections?: string[];
  /**
   * What this tenant is ACCREDITED TO — ACCREDITATION_STANDARDS keys (the four
   * ISO standards, the three FORS grades). Set per tenant in Platform Admin ->
   * Configuration.
   *
   * UNLIKE selectedSections there is NO FALLBACK: empty means the tenant holds
   * nothing, so the Accreditations entries do not render. Optional on the type
   * only for payloads cached before this shipped, which read as undefined and
   * are treated as [].
   */
  activeStandards?: string[];
  /**
   * DH-CONTENT-PROFILE-001 Phase 2 — the sectors this tenant operates in
   * (canonical SECTORS slugs). The per-tenant content switch. Optional for
   * older cached payloads; treat undefined as [] (= applies to all / nothing
   * hidden).
   */
  profileSectors?: string[];
  /**
   * Spec 18 — EFFECTIVE operating sectors (profileSectors with per-tenant
   * sectorOverrides applied), which sector content is filtered by. Optional
   * for older cached payloads; fall back to profileSectors then [].
   */
  effectiveSectors?: string[];
  createdAt?: string;
}

export async function fetchTenantProfile(signal?: AbortSignal): Promise<TenantProfile> {
  return apiFetch<TenantProfile>('/api/tenant/profile', {
    method: 'GET',
    signal,
  });
}

/**
 * PATCH /api/tenant/multi-site — toggle the tenant's multi-site mode.
 * Director only. Returns the new state.
 */
export async function setMultiSiteMode(
  isMultiSite: boolean,
): Promise<{ isMultiSite: boolean }> {
  return apiFetch<{ isMultiSite: boolean }>('/api/tenant/multi-site', {
    method: 'PATCH',
    body: { isMultiSite },
  });
}

/**
 * Stage 1.6e (2026-05-12) — PATCH /api/tenant/biometric-enabled.
 * Director / admin only. Off by default; enabling requires the
 * tenant to have completed their DPIA + employee notice. Audit-logged
 * server-side. Returns the new state.
 */
export async function setBiometricEnabled(
  enabled: boolean,
): Promise<{ biometricEnabled: boolean }> {
  return apiFetch<{ biometricEnabled: boolean }>('/api/tenant/biometric-enabled', {
    method: 'PATCH',
    body: { enabled },
  });
}

/**
 * 2026-10-01 — whether an approver must be matched to an employee record
 * before they can approve leave. Director / admin only.
 *
 * The self-approval refusal can only recognise somebody who IS matched, so
 * unmatched it fails open. This decides whether that is refused or merely
 * flagged on Users & Permissions — a tenant's call, because a two-person firm
 * and an ISO-audited haulier want opposite answers. Default off.
 */
export async function setRequireEmployeeMatch(
  required: boolean,
): Promise<{ requireEmployeeMatchToApprove: boolean }> {
  return apiFetch<{ requireEmployeeMatchToApprove: boolean }>(
    '/api/tenant/require-employee-match',
    { method: 'PATCH', body: { required } },
  );
}


export type LeaveLimitMode = 'warn' | 'refuse';

/** Whether a booking over a statutory leave limit is warned about or refused. Director / admin only. */
export async function setLeaveLimitMode(mode: LeaveLimitMode): Promise<{ leaveLimitMode: LeaveLimitMode }> {
  return apiFetch<{ leaveLimitMode: LeaveLimitMode }>('/api/tenant/leave-limit-mode', {
    method: 'PATCH',
    body: { mode },
  });
}

/** Setup journey accounts step: no previous accounts system, so no takeover. */
export async function setAccountsStartFresh(): Promise<{ at: string; by: string }> {
  return apiFetch<{ at: string; by: string }>('/api/tenant/accounts-start-fresh', {
    method: 'POST',
    body: {},
  });
}

/**
 * The company bank account — the one a customer pays into and wages are paid
 * from. Captured on Company Details, read by invoices and by BACS.
 *
 * The GET returns the account number MASKED. The full number leaves the server
 * only onto an invoice or into a BACS file; a form has no business prefilling
 * it, and this is the same shape employee bank details have always used.
 */
export interface CompanyBankAccount {
  configured: boolean;
  bankName?: string;
  accountName?: string;
  sortCode?: string;
  accountNumberLast4?: string;
  /** What still needs entering, when configured is false. */
  missing: string[];
}

export interface CompanyBankAccountInput {
  bankName: string;
  accountName: string;
  sortCode: string;
  accountNumber: string;
}

export async function fetchCompanyBank(): Promise<CompanyBankAccount> {
  return apiFetch<CompanyBankAccount>('/api/tenant/company-bank');
}

export async function updateCompanyBank(
  input: CompanyBankAccountInput,
): Promise<{ accountNumberLast4: string }> {
  return apiFetch('/api/tenant/company-bank', {
    method: 'PUT',
    body: input,
  });
}

// ─── The company's holiday year ──────────────────────────────────────────────
//
// Separate from company-details for the same reason the bank account is: this
// has real columns and CHECK constraints, and every holiday figure on the
// platform is measured against it.

export interface TenantLeaveYear {
  mode: 'calendar' | 'payroll' | 'business';
  startDay: number;
  startMonth: number;
  currentLeaveYear: number;
  /** "1 Apr 2026 to 31 Mar 2027" — formatted by the server so it reads the same everywhere. */
  label: string;
  /**
   * Has a person actually answered, or is this still the default?
   *
   * `mode` cannot tell you: it defaults to 'calendar', so a tenant who chose
   * the calendar year and one who has never been asked look identical. Anything
   * deciding whether the question is settled must use this.
   */
  answered: boolean;
}

export async function fetchTenantLeaveYear(signal?: AbortSignal): Promise<TenantLeaveYear> {
  return apiFetch<TenantLeaveYear>('/api/tenant/leave-year', { method: 'GET', signal });
}

export async function updateTenantLeaveYear(input: {
  mode: 'calendar' | 'payroll' | 'business';
  startDay: number;
  startMonth: number;
}): Promise<{ mode: string; startDay: number; startMonth: number; label: string }> {
  return apiFetch('/api/tenant/leave-year', { method: 'PATCH', body: input });
}

/*
 * WHAT THE COMPANY GIVES.
 *
 * Shane, 2026-08-25: *"below this section we need to add what the company
 * gives. ie weeks / days + Bank holidays"*, and *"someone who works 3 days a
 * week is still entitled to 4 weeks holiday but it is the 3 days x 4"*.
 *
 * Held in WEEKS or days, because weeks are what make part-time come out right:
 * everybody gets the same number of weeks and a three-day week is three days
 * long. gov.uk states the statutory entitlement in weeks for the same reason.
 */
export interface TenantHolidayAllowance {
  value: number;
  unit: 'weeks' | 'days';
  /**
   * on_top — the figure above is holiday and bank holidays are extra.
   * included — the figure already contains them.
   *
   * Both are lawful (gov.uk), so it is recorded rather than assumed. Guessing
   * wrong moves every employee's entitlement by more than a week.
   */
  bankHolidays: 'on_top' | 'included';
  /** The denominator that pro-rates bank holidays for part-timers. */
  fullTimeDaysPerWeek: number;
  /**
   * Has a person recorded this? False means the CountryPack statutory figure
   * is showing and the values are that figure, not a company decision.
   */
  answered: boolean;
}

export async function fetchTenantHolidayAllowance(
  signal?: AbortSignal,
): Promise<TenantHolidayAllowance> {
  return apiFetch<TenantHolidayAllowance>('/api/tenant/holiday-allowance', {
    method: 'GET',
    signal,
  });
}

export async function updateTenantHolidayAllowance(input: {
  value: number;
  unit: 'weeks' | 'days';
  bankHolidays: 'on_top' | 'included';
  fullTimeDaysPerWeek: number;
}): Promise<TenantHolidayAllowance> {
  return apiFetch('/api/tenant/holiday-allowance', { method: 'PATCH', body: input });
}

/*
 * Administration › Employee App (2026-09-28) — how the tenant runs the
 * Employee app. Shape of services/tenant/employeeAppConfigService.ts.
 * Absent row = every default with configured: false; nothing is on until a
 * director turns it on, apart from kiosk PIN.
 */
export type ClockInMethod = 'app' | 'kiosk' | 'staffid';

export interface EmployeeAppConfig {
  clockInMethods: ClockInMethod[];
  allowBreaks: boolean;
  requireGeofence: boolean;
  requirePhoto: boolean;
  geofenceBlocks: boolean;
  photoBlocks: boolean;
  screeningEnabled: boolean;
  canRequestHoliday: boolean;
  canReportSickness: boolean;
  sendPolicies: boolean;
  sendMemos: boolean;
  sendPayslips: boolean;
  sendToolbox: boolean;
  /** Staff may set their own profile picture from the app (2026-10-14). */
  allowProfilePhoto: boolean;
  tickMobileAppAccessByDefault: boolean;
  sendSignInLink: boolean;
  /** False while these are the defaults because nobody has saved them. */
  configured: boolean;
}

/** GET /api/tenant/employee-app-config — director, manager or admin. */
export async function fetchEmployeeAppConfig(signal?: AbortSignal): Promise<EmployeeAppConfig> {
  return apiFetch<EmployeeAppConfig>('/api/tenant/employee-app-config', { method: 'GET', signal });
}

/**
 * PUT /api/tenant/employee-app-config — director or manager. A partial
 * update: only the fields sent change. An empty body is refused.
 */
export async function saveEmployeeAppConfig(
  input: Partial<Omit<EmployeeAppConfig, 'configured'>>,
): Promise<EmployeeAppConfig> {
  return apiFetch<EmployeeAppConfig>('/api/tenant/employee-app-config', { method: 'PUT', body: input });
}
